import { useState } from "react";
import { format } from "date-fns";
import { Search, ArrowUpDown, MapPin, Users } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { EventPartnerTrip, TripState } from "@/types/eventPartner";

interface EventTripsTableProps {
  trips: EventPartnerTrip[];
  isLoading?: boolean;
}

const STATUS_VARIANTS: Record<TripState, "default" | "secondary" | "destructive" | "outline"> = {
  UNCLAIMED: "outline",
  BOOKED: "secondary",
  CLAIMED: "secondary",
  DRIVER_ASSIGNED: "secondary",
  DRIVER_CONFIRMED: "secondary",
  COMPLETED: "default",
  ABANDONED: "destructive",
  CANCELED: "destructive",
};

const STATUS_LABELS: Record<TripState, string> = {
  UNCLAIMED: "Unclaimed",
  BOOKED: "Booked",
  CLAIMED: "Claimed",
  DRIVER_ASSIGNED: "Driver Assigned",
  DRIVER_CONFIRMED: "Driver Confirmed",
  COMPLETED: "Completed",
  ABANDONED: "Abandoned",
  CANCELED: "Canceled",
};

// Only show trips that are BOOKED or later (exclude UNCLAIMED)
const VISIBLE_STATUSES: TripState[] = [
  "BOOKED",
  "CLAIMED",
  "DRIVER_ASSIGNED",
  "DRIVER_CONFIRMED",
  "COMPLETED",
  "ABANDONED",
  "CANCELED",
];

export const EventTripsTable = ({ trips, isLoading }: EventTripsTableProps) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<TripState | "ALL">("ALL");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Filter out UNCLAIMED trips - only show BOOKED or later
  const bookedOrLaterTrips = trips.filter((trip) => trip.trip_state !== "UNCLAIMED");

  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(parseFloat(amount) || 0);
  };

  // Filter and sort trips (already filtered to exclude UNCLAIMED)
  const filteredTrips = bookedOrLaterTrips
    .filter((trip) => {
      const matchesSearch =
        search === "" ||
        trip.rider.username.toLowerCase().includes(search.toLowerCase()) ||
        trip.rider.email.toLowerCase().includes(search.toLowerCase()) ||
        trip.from_address.formatted.toLowerCase().includes(search.toLowerCase()) ||
        trip.to_address.formatted.toLowerCase().includes(search.toLowerCase()) ||
        trip.user_friendly_name.toLowerCase().includes(search.toLowerCase());

      const matchesStatus =
        statusFilter === "ALL" || trip.trip_state === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      const dateA = new Date(a.pickup_at).getTime();
      const dateB = new Date(b.pickup_at).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Trip Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse h-12 bg-muted rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border border-gray-200 shadow-sm">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="text-gray-900">Trip Details ({filteredTrips.length})</CardTitle>
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search trips..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-full sm:w-64 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              />
            </div>

            {/* Status Filter */}
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as TripState | "ALL")}
            >
              <SelectTrigger className="w-full sm:w-40 bg-white border-gray-300 text-gray-900">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900">
                <SelectItem value="ALL">All statuses</SelectItem>
                {VISIBLE_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort Toggle */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              title={`Sort by pickup time (${sortOrder === "asc" ? "earliest first" : "latest first"})`}
              className="bg-white border-gray-300 text-gray-900 hover:bg-gray-100"
            >
              <ArrowUpDown className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredTrips.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {bookedOrLaterTrips.length === 0
              ? "No booked trips found for this event."
              : "No trips match your filters."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rider</TableHead>
                  <TableHead>Pickup Time</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead className="text-center">Passengers</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTrips.map((trip) => (
                  <TableRow key={trip.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">
                          {trip.rider.username}
                        </p>
                        <p className="text-xs text-gray-500">
                          {trip.rider.email}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-gray-900">
                      {format(new Date(trip.pickup_at), "MMM d, yyyy")}
                      <br />
                      <span className="text-gray-500 text-xs">
                        {format(new Date(trip.pickup_at), "h:mm a")}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-start gap-1 max-w-48">
                        <MapPin className="h-3 w-3 mt-1 flex-shrink-0 text-gray-400" />
                        <span className="text-xs text-gray-700 truncate" title={trip.from_address.formatted}>
                          {trip.from_address.formatted}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-start gap-1 max-w-48">
                        <MapPin className="h-3 w-3 mt-1 flex-shrink-0 text-green-600" />
                        <span className="text-xs text-gray-700 truncate" title={trip.to_address.formatted}>
                          {trip.to_address.formatted}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-900">{trip.user_friendly_name}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1 text-gray-700">
                        <Users className="h-3 w-3 text-gray-400" />
                        <span>{trip.passenger_count}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium text-green-600">
                      {formatCurrency(trip.rider_price)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANTS[trip.trip_state]}>
                        {STATUS_LABELS[trip.trip_state]}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
